import React, { useState, useMemo } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { SalesOrder } from '../../../../types/sales.types';
import { useExchangeModal } from './ExchangeModal.hooks';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import {
  CloseConfirmationModal,
  ConfirmExchangeConfirmationModal,
} from '../../../modals/confirmation';
import * as S from './ExchangeDrawer.styles';
import ExchangeFlowContent from './ExchangeFlowContent';
import MissingRequirementsHint from '../../shared/MissingRequirementsHint';

export interface ExchangeDrawerProps {
  open: boolean;
  onClose: () => void;
  order: SalesOrder | null;
  onExchangeCompleted?: () => void;
}

const totalSteps = 3;

const ExchangeDrawer: React.FC<ExchangeDrawerProps> = ({
  open,
  onClose,
  order,
  onExchangeCompleted,
}) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const {
    currentStep,
    isLoading,
    error,
    exchangeData,
    exchangeSummary,
    eligibility,
    canProceedToNextStep,
    handleNextStep,
    handlePreviousStep,
    handleSelectItemsToExchange,
    handleAddNewProduct,
    handleRemoveNewProduct,
    handleUpdateNewProduct,
    updateNewProductMarkup,
    updateNewProductDiscount,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePayment,
    handleUpdateExchangeNotes,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    updateMarkupConfig,
    updateDiscountConfig,
    applyUniformMarkupToNewProducts,
    applyUniformDiscountToNewProducts,
    handlePartialPaymentToggle,
    handleMultiplePaymentsToggle,
    getProductFinalPrice,
    setProductDelivered,
    paymentFlowPhase,
    setPaymentFlowPhase,
    paymentsLocked,
    setPaymentsLocked,
    clearExchangePayments,
    clearNewProducts,
    showConfirmModal,
    openConfirmModal,
    closeConfirmModal,
    handleConfirmExchange,
    requiresZeroSaleAck,
    formatCurrency,
    clearError,
    canModifySaleDate,
    missingExchangeDate,
    exchangeDateMode,
    exchangeDate,
    exchangeDateModeError,
    handleExchangeDateModeChange,
    setExchangeDate,
  } = useExchangeModal({
    open,
    order,
    onExchangeCompleted,
  });

  // Verificar si hay datos ingresados para mostrar confirmación de cierre
  const hasDataEntered = useMemo(() => {
    return (
      exchangeData.itemsToExchange.length > 0 ||
      exchangeData.newProducts.length > 0 ||
      (exchangeData.payments?.length ?? 0) > 0 ||
      (exchangeData.exchangeNotes?.trim() ?? '').length > 0
    );
  }, [exchangeData]);

  // Manejo de cierre con confirmación
  const handleCloseClick = () => {
    if (hasDataEntered && !isLoading) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  };

  const cancelOperation = () => {
    setShowCancelConfirmation(false);
    onClose();
  };

  const handleCancelCancel = () => {
    setShowCancelConfirmation(false);
  };

  // Interceptar ESC para mostrar confirmación de cierre
  useEscapeKey(
    () => {
      if (open && !isLoading && !showCancelConfirmation && !showConfirmModal) {
        handleCloseClick();
      }
    },
    open && !isLoading && !showCancelConfirmation && !showConfirmModal,
  );

  if (!open || !order) return null;

  const flowContentProps = {
    currentStep,
    order,
    eligibility,
    exchangeData,
    exchangeSummary,
    formatCurrency,
    paymentFlowPhase,
    setPaymentFlowPhase,
    paymentsLocked,
    setPaymentsLocked,
    onSelectItemsToExchange: handleSelectItemsToExchange,
    onAddNewProduct: handleAddNewProduct,
    onRemoveNewProduct: handleRemoveNewProduct,
    onClearNewProducts: clearNewProducts,
    onUpdateNewProduct: handleUpdateNewProduct,
    updateNewProductMarkup,
    updateNewProductDiscount,
    onUpdateExchangeNotes: handleUpdateExchangeNotes,
    onClearPayments: clearExchangePayments,
    onAddPayment: handleAddPayment,
    onRemovePayment: handleRemovePayment,
    onUpdatePayment: handleUpdatePayment,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    updateMarkupConfig,
    updateDiscountConfig,
    applyUniformMarkupToNewProducts,
    applyUniformDiscountToNewProducts,
    onPartialPaymentToggle: handlePartialPaymentToggle,
    onMultiplePaymentsToggle: handleMultiplePaymentsToggle,
    getProductFinalPrice,
    onToggleDelivered: setProductDelivered,
    canModifySaleDate,
    exchangeDateMode,
    exchangeDate,
    exchangeDateModeError,
    onExchangeDateModeChange: handleExchangeDateModeChange,
    onExchangeDateChange: setExchangeDate,
  };

  const getActiveStep = () => {
    if (currentStep === 'SELECTION') return 1;
    if (currentStep === 'PAYMENT') return 2;
    return 3;
  };

  const getFormProgress = () => {
    const step = getActiveStep();
    return Math.round((step / totalSteps) * 100);
  };

  return (
    <>
      <S.StyledDrawer
        anchor="right"
        open={open}
        onClose={handleCloseClick}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '1000px', md: '1200px' },
            maxWidth: '1200px',
          },
        }}
      >
        <S.DrawerContainer>
          {/* Header */}
          <S.DrawerHeader>
            <S.DrawerTitle>Intercambio de piezas: {order.orderCode}</S.DrawerTitle>
            <S.CloseButton onClick={handleCloseClick}>
              <CloseIcon />
            </S.CloseButton>
          </S.DrawerHeader>

          {/* Barra de progreso */}
          <S.ProgressContainer>
            <S.ProgressBar>
              <S.ProgressBarFill style={{ width: `${getFormProgress()}%` }} />
            </S.ProgressBar>
            <S.ProgressInfo>
              <span>
                Paso {getActiveStep()} de {totalSteps}
              </span>
              <span>{getFormProgress()}% completado</span>
            </S.ProgressInfo>
          </S.ProgressContainer>

          {/* Content */}
          <S.ContentContainer>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mx: 3, mt: 2, mb: 0 }} onClose={clearError}>
                {error}
              </Alert>
            )}

            {/* Step Content */}
            {isLoading && currentStep === 'SELECTION' ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : (
              <ExchangeFlowContent {...flowContentProps} />
            )}
          </S.ContentContainer>

          {/* Footer */}
          <S.DrawerFooter>
            <S.PreviousButton
              variant="tonal"
              onClick={currentStep === 'SELECTION' ? handleCloseClick : handlePreviousStep}
              disabled={isLoading}
            >
              {currentStep === 'SELECTION' ? 'Cancelar' : 'Anterior'}
            </S.PreviousButton>

            <S.FooterButtonsContainer>
              {currentStep !== 'CONFIRMATION' ? (
                <S.NextButton
                  variant="contained"
                  onClick={handleNextStep}
                  disabled={isLoading || !canProceedToNextStep}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isLoading ? 'Procesando...' : 'Siguiente'}
                </S.NextButton>
              ) : (
                <>
                  <MissingRequirementsHint
                    missingDate={missingExchangeDate}
                    sx={{ alignSelf: 'center', textAlign: 'right' }}
                  />
                  <S.ConfirmButton
                    variant="contained"
                    onClick={openConfirmModal}
                    disabled={isLoading || !canProceedToNextStep}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                  >
                    {isLoading ? 'Procesando...' : 'Confirmar Intercambio'}
                  </S.ConfirmButton>
                </>
              )}
            </S.FooterButtonsContainer>
          </S.DrawerFooter>
        </S.DrawerContainer>
      </S.StyledDrawer>

      {/* Modal de confirmación de cierre */}
      <CloseConfirmationModal
        open={showCancelConfirmation}
        onClose={handleCancelCancel}
        onConfirm={cancelOperation}
      />

      <ConfirmExchangeConfirmationModal
        open={showConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmExchange}
        loading={isLoading}
        requiresZeroSaleAck={requiresZeroSaleAck}
      />
    </>
  );
};

export default ExchangeDrawer;
